
export type DevWorklogDeliverable = {
  task_id: string;
  description: string;
  commit_hashes: string[];
  verification_endpoint: string | null;
  hours_logged: number;
  parent_task_total_hours: number;
  status: string;
};

export type DevWorklogPayload = {
  developer_id: string;
  sprint_week: string;
  period: { start: string; end: string };
  repositories_touched: string[];
  deliverables: DevWorklogDeliverable[];
  total_hours_this_week: number;
  blockers: string[];
};

function isDevWorklogPayload(value: unknown): value is DevWorklogPayload {
  if (!value || typeof value !== "object") return false;
  const o = value as Record<string, unknown>;
  return (
    typeof o.developer_id === "string" && Array.isArray(o.deliverables)
  );
}

export function normalizeDevWorklogResponse(raw: unknown): DevWorklogPayload | null {
  if (isDevWorklogPayload(raw)) return raw;
  if (raw && typeof raw === "object") {
    const nested = (raw as Record<string, unknown>).data;
    if (nested) return normalizeDevWorklogResponse(nested);
  }
  return null;
}

function formatIsoDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatStatus(status: string): string {
  return status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function statusBadgeClass(status: string): string {
  const s = status.toLowerCase();
  if (s === "completed" || s === "complete") return "text-bg-success";
  if (s === "blocked") return "text-bg-danger";
  if (s === "in_progress" || s === "in progress") return "text-bg-primary";
  return "text-bg-secondary";
}

function formatHours(hours: number): string {
  if (!Number.isFinite(hours)) return "—";
  return `${hours % 1 === 0 ? hours : hours.toFixed(1)} h`;
}

function DevWorklogReportView({ report }: { report: DevWorklogPayload }) {
  const deliverables = report.deliverables ?? [];
  const repos = report.repositories_touched ?? [];
  const blockers = (report.blockers ?? []).filter(
    (b) => typeof b === "string" && b.trim().length > 0,
  );

  return (
    <div className="admin-worklog-report">
      <div className="admin-worklog-summary row g-3 mb-4">
        <div className="col-sm-6 col-lg-3">
          <div className="admin-worklog-stat-card h-100">
            <div className="admin-worklog-stat-label">Developer</div>
            <div className="admin-worklog-stat-value">{report.developer_id}</div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-3">
          <div className="admin-worklog-stat-card h-100">
            <div className="admin-worklog-stat-label">Sprint week</div>
            <div className="admin-worklog-stat-value">{report.sprint_week}</div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-3">
          <div className="admin-worklog-stat-card h-100">
            <div className="admin-worklog-stat-label">Period</div>
            <div className="admin-worklog-stat-value admin-worklog-stat-value--sm">
              {formatIsoDate(report.period?.start ?? "")}
              <span className="text-muted mx-1">→</span>
              {formatIsoDate(report.period?.end ?? "")}
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-3">
          <div className="admin-worklog-stat-card admin-worklog-stat-card--accent h-100">
            <div className="admin-worklog-stat-label">Total hours</div>
            <div className="admin-worklog-stat-value">
              {formatHours(report.total_hours_this_week)}
            </div>
          </div>
        </div>
      </div>

      {repos.length > 0 ? (
        <section className="admin-worklog-section mb-4">
          <h6 className="admin-worklog-section-title">Repositories touched</h6>
          <div className="admin-worklog-repo-tags">
            {repos.map((repo) => (
              <span
                key={repo}
                className="badge rounded-pill text-black admin-worklog-repo-badge"
              >
                {repo}
              </span>
            ))}
          </div>
        </section>
      ) : null}

      <section className="admin-worklog-section mb-4">
        <div className="d-flex align-items-center justify-content-between gap-2 mb-2 flex-wrap">
          <h6 className="admin-worklog-section-title mb-0">Deliverables</h6>
          <span className="text-muted small">
            {deliverables.length} item{deliverables.length === 1 ? "" : "s"}
          </span>
        </div>
        {deliverables.length === 0 ? (
          <p className="text-muted small mb-0">No deliverables in this period.</p>
        ) : (
          <div className="table-responsive admin-worklog-table-wrap">
            <table className="table table-sm table-hover align-middle admin-worklog-table mb-0">
              <thead className="table-light">
                <tr>
                  <th scope="col">Task</th>
                  <th scope="col">Description</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="text-end text-nowrap">
                    Hours
                  </th>
                  <th scope="col" className="text-end text-nowrap">
                    Parent total
                  </th>
                  <th scope="col">Commits</th>
                  <th scope="col">Verification</th>
                </tr>
              </thead>
              <tbody>
                {deliverables.map((row, index) => (
                  <tr key={`${row.task_id}-${row.description}-${index}`}>
                    <td className="fw-medium text-nowrap">{row.task_id}</td>
                    <td className="admin-worklog-desc-cell">{row.description}</td>
                    <td>
                      <span
                        className={`badge ${statusBadgeClass(row.status)}`}
                      >
                        {formatStatus(row.status)}
                      </span>
                    </td>
                    <td className="text-end text-nowrap">
                      {formatHours(row.hours_logged)}
                    </td>
                    <td className="text-end text-nowrap text-muted">
                      {formatHours(row.parent_task_total_hours)}
                    </td>
                    <td className="admin-worklog-commits-cell">
                      {row.commit_hashes?.length ? (
                        <div className="admin-worklog-commit-tags">
                          {row.commit_hashes.map((hash) => (
                            <code key={hash} className="admin-worklog-commit-tag">
                              {hash}
                            </code>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="admin-worklog-verify-cell">
                      {row.verification_endpoint ? (
                        <a
                          href={row.verification_endpoint}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="admin-worklog-verify-link"
                          title={row.verification_endpoint}
                        >
                          Open
                        </a>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {blockers.length > 0 ? (
        <section className="admin-worklog-section">
          <h6 className="admin-worklog-section-title admin-worklog-section-title--warning">
            Blockers
          </h6>
          <ul className="admin-worklog-blockers-list mb-0">
            {blockers.map((blocker, index) => (
              <li key={`${index}-${blocker.slice(0, 24)}`}>{blocker}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

export default function DevWorklogReport({ data }: { data: unknown }) {
  const report = normalizeDevWorklogResponse(data);
  if (!report) {
    return (
      <pre className="admin-worklog-json mb-0">
        {typeof data === "string" ? data : JSON.stringify(data, null, 2)}
      </pre>
    );
  }
  return <DevWorklogReportView report={report} />;
}
